import AddButtonRounded from '@Components/AddButtonRounded';
import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import { DeleteOutline } from '@mui/icons-material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

type InputOtherSectionSubcommitteesProps = {
  title: string;
  disabled: boolean;
  name: string;
  isCompletedTh?: boolean;
  isCompletedEn?: boolean;
};

const InputOtherSectionSubcommittees = ({
  title,
  disabled,
  name,
  isCompletedTh,
  isCompletedEn,
}: InputOtherSectionSubcommitteesProps) => {
  const { control, watch, setValue, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
    shouldUnregister: true,
  });

  const prevDisabledRef = useRef(disabled);
  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      trigger(name);
      initialRender.current = false;
    }

    const subscription = watch((value, { name: fieldName }) => {
      if (fieldName?.startsWith(name)) {
        trigger(name);
      }
    });

    return () => subscription.unsubscribe();
  }, [name, trigger]);

  useEffect(() => {
    const currentValue = watch(name);
    if (
      !currentValue ||
      (Array.isArray(currentValue) && currentValue.length === 0)
    ) {
      if (fields.length === 0) {
        append({ nameTh: '', nameEn: '' });
      }
    }
  }, [name, fields.length, append]);

  useEffect(() => {
    if (prevDisabledRef.current && !disabled) {
      setValue(name, [{ nameTh: '', nameEn: '' }]);
    }
    prevDisabledRef.current = disabled;
  }, [disabled, name, setValue]);

  return (
    <Box>
      <Flex alignItems={'center'} my={1}>
        <Typography>{title}</Typography>
        <Tooltip
          placement="top"
          sx={{ whiteSpace: 'pre-wrap' }}
          title={
            <>
              หากต้องการเพิ่มตัวเลือกอื่น ๆ กรุณาเลือก "อื่น ๆ"
              <br />
              ในรายการด้านบนเพื่อเพิ่มข้อมูล
            </>
          }
        >
          <IconButton size="small" sx={{ mt: 0.5 }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Flex>
      {fields.map((field, index) => (
        <Flex
          key={field.id}
          bgcolor={'grey.100'}
          p={1}
          mb={1}
          borderRadius={1}
          width={1}
          alignItems={'center'}
        >
          <Flex flexDirection={'column'} gap={1} width={1}>
            <FormInput
              name={`${name}.${index}.nameTh`}
              fullWidth
              placeholder="กรุณาระบุอื่นๆ (ภาษาไทย)"
              slotProps={{
                input: {
                  disabled: !disabled || isCompletedTh,
                  onBlur: () => trigger(`${name}.${index}.nameEn`),
                },
              }}
              sx={{ width: 1 }}
            />
            <FormInput
              name={`${name}.${index}.nameEn`}
              fullWidth
              placeholder="กรุณาระบุอื่นๆ (ภาษาอังกฤษ)"
              slotProps={{
                input: {
                  disabled: !disabled || isCompletedEn,
                  onBlur: () => trigger(`${name}.${index}.nameTh`),
                },
              }}
              sx={{ width: 1 }}
            />
          </Flex>
          {fields.length > 1 && !isCompletedTh && !isCompletedEn && (
            <Box ml={2}>
              <DeleteOutline
                fontSize="small"
                sx={{
                  cursor: 'pointer',
                  visibility:
                    isCompletedTh || isCompletedEn ? 'hidden' : 'visible',
                }}
                onClick={() => remove(index)}
              />
            </Box>
          )}
        </Flex>
      ))}
      <AddButtonRounded
        name="เพิ่มรายการอื่นๆ"
        onClick={() => {
          append({ nameTh: '', nameEn: '' });
        }}
        disabled={!disabled || isCompletedTh || isCompletedEn}
      />
    </Box>
  );
};

export default InputOtherSectionSubcommittees;
